import type { Stats } from "@shared/types.js";
import { fnum, fpct } from "../lib/format.js";
import { useChartTip } from "./ChartTip.js";

interface Stage {
  label: string;
  value: number;
  color: string;
  note: string;
}

/**
 * One shared denominator for the whole pipeline: every bar is a share of the
 * records in scope, so the stages are directly comparable and the drop-off
 * between them is the point. Replaces five donuts that each had their own base.
 */
export function Funnel({ s, onPick }: { s: Stats; onPick?: (status: string) => void }) {
  const stages: Stage[] = [
    { label: "In scope", value: s.total, color: "var(--line-2)", note: "every record the filters allow" },
    { label: "CR received", value: s.received, color: "var(--s-await)", note: "contract rate in hand" },
    { label: "Rate entered", value: s.entered, color: "var(--s-done)", note: "priced or estimated" },
    { label: "Crosschecked", value: s.crosschecked, color: "var(--s-est)", note: "reviewed" },
    { label: "Exported", value: s.exported, color: "var(--focus)", note: "live on the extranet" },
  ];
  const statusFor: (string | undefined)[] = [undefined, "received", undefined, "crosschecked", "exported"];
  const base = s.total || 1;
  const { bind, tipNode } = useChartTip();

  return (
    <div className="funnel">
      {tipNode}
      {stages.map((st, i) => {
        const prev = i > 0 ? stages[i - 1]!.value : null;
        const drop = prev === null ? null : prev - st.value;
        const status = statusFor[i];
        const clickable = !!(onPick && status);
        return (
          <div className="fn-row" key={st.label}>
            <div className="fn-lab">
              {st.label}
              <small>{st.note}</small>
            </div>
            <div className="fn-track">
              <div
                className={`fn-fill${clickable ? " tap" : ""}`}
                style={{ width: `${Math.max(0.5, (st.value / base) * 100)}%`, background: st.color }}
                onClick={clickable ? () => onPick!(status!) : undefined}
                role={clickable ? "button" : undefined}
                tabIndex={clickable ? 0 : undefined}
                onKeyDown={clickable
                  ? (e) => { if (e.key === "Enter") onPick!(status!); }
                  : undefined}
                {...bind({
                  label: st.label,
                  value: `${fnum(st.value)} of ${fnum(s.total)}`,
                  note: drop !== null && drop > 0
                    ? `${fpct(st.value, base, 0)} · ${fnum(drop)} dropped at this stage`
                    : `${fpct(st.value, base, 0)}${clickable ? " · click to filter" : ""}`,
                  color: st.color,
                })}
              >
                <span className="fn-num">{fnum(st.value)}</span>
              </div>
            </div>
            <div className="fn-pct">
              {fpct(st.value, base, 0)}
              {drop !== null && drop > 0 && (
                <small>&minus;{fnum(drop)} here</small>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
